import { useEffect, useMemo, useState } from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  ApiOutlined,
  ArrowRightOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { authService } from '../../services/auth';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAdmin } from '../../contexts/AdminContext';
import './Login.css';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [typedTitle, setTypedTitle] = useState('');
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { fetchProfile } = useAdmin();
  const titleText = 'TocoTalk 的核心运营面。';

  const insightItems = useMemo(() => ([
    {
      icon: <SafetyCertificateOutlined />,
      title: '可信认证',
      description: '管理员会话与访问入口统一管理。',
    },
    {
      icon: <ApiOutlined />,
      title: '多端协同',
      description: '覆盖设备、支付、云盘与运营配置。',
    },
    {
      icon: <SafetyOutlined />,
      title: '稳定控制',
      description: '在一个控制台完成关键业务操作。',
    },
  ]), []);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await authService.login(values);
      const authData = response?.data || response;
      if (authData?.accessToken) {
        localStorage.setItem('admin_token', authData.accessToken);
        if (authData.refreshToken) {
          localStorage.setItem('admin_refresh_token', authData.refreshToken);
        }
        await fetchProfile();
        message.success('登录成功');
        navigate('/dashboard');
      } else {
        message.error(response?.message || '登录失败，请检查账号和密码');
      }
    } catch (error) {
      message.error(error.response?.data?.message || '登录失败，请检查账号和密码');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let frameTimer;
    let restartTimer;
    let currentIndex = 0;

    const typeNext = () => {
      currentIndex += 1;
      setTypedTitle(titleText.slice(0, currentIndex));

      if (currentIndex < titleText.length) {
        frameTimer = setTimeout(typeNext, 110);
      } else {
        restartTimer = setTimeout(() => {
          currentIndex = 0;
          setTypedTitle('');
          frameTimer = setTimeout(typeNext, 500);
        }, 1800);
      }
    };

    frameTimer = setTimeout(typeNext, 350);

    return () => {
      clearTimeout(frameTimer);
      clearTimeout(restartTimer);
    };
  }, [titleText]);

  return (
    <div className={`login-container ${isDark ? 'dark' : 'light'}`}>
      <div className="login-backdrop" />
      <div className="login-shell">
        <section className="login-showcase">
          <div className="login-showcase__eyebrow">
            <span className="login-showcase__dot" />
            MindAX Admin Portal
          </div>
          <div className="login-showcase__content">
            <h1>
              <span className="login-heading-static">{'统一掌控'}</span>
              <span className="login-heading-typing" aria-label={'动态标题'}>
                <span className="login-heading-typing__text">{typedTitle}</span>
              </span>
            </h1>
            <div className="login-typing" aria-label={'动态说明'}>
              <span className="login-typing__text">{'设备管理 · 会员履约 · 家庭云盘 · 运营配置'}</span>
            </div>
          </div>

          <div className="login-insights" aria-label={'登录页能力摘要'}>
            {insightItems.map((item) => (
              <div key={item.title} className="login-insight">
                <div className="login-insight__icon">{item.icon}</div>
                <div className="login-insight__body">
                  <strong>{item.title}</strong>
                  <span>{item.description}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <Card className="login-card">
          <div className="login-card__inner">
            <div className="login-header">
              <div className="login-header__badge">{'管理后台登录'}</div>
              <h2>{'欢迎回来'}</h2>
              <p>{'请输入管理员账号与密码，进入业务控制台。'}</p>
            </div>

            <Form
              name="login"
              onFinish={onFinish}
              autoComplete="off"
              size="large"
              layout="vertical"
            >
              <Form.Item
                label={'账号'}
                name="account"
                rules={[{ required: true, message: '请输入账号!' }]}
              >
                <Input prefix={<UserOutlined />} placeholder={'请输入管理员账号'} />
              </Form.Item>

              <Form.Item
                label={'密码'}
                name="password"
                rules={[{ required: true, message: '请输入密码!' }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={'请输入登录密码'} />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                  icon={!loading ? <ArrowRightOutlined /> : null}
                >
                  {loading ? '登录中...' : '进入控制台'}
                </Button>
              </Form.Item>
            </Form>

            <div className="login-footer-note">
              <span className="login-footer-note__line" />
              {'仅限授权管理员访问 · 请勿在公共设备保存凭证'}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;
